import React, { useState } from 'react';
import { FiCheck, FiX } from 'react-icons/fi';
import type { HREmployee, PtoType, TimeOffRequest } from './hrData';
import type { CreateTimeOffPayload } from '../../services/hrService';
import { usDate } from './licenseUtils';

interface Props {
  me: HREmployee;
  admin: boolean;
  employees: HREmployee[];
  myRequests: TimeOffRequest[];
  /** Admin only — every pending request in the agency. */
  pendingRequests: TimeOffRequest[];
  ptoTypes: PtoType[];
  busy: boolean;
  /** Resolves true once saved; false keeps the form filled in (the page shows the error). */
  onSubmit: (payload: CreateTimeOffPayload) => Promise<boolean>;
  onRespond: (id: number, status: 'approved' | 'denied') => void;
  onCancel: (id: number) => void;
}

const dateRange = (r: TimeOffRequest) => `${usDate(r.startDate)}${r.endDate !== r.startDate ? ` – ${usDate(r.endDate)}` : ''}`;

/** Balances can go negative by design — we warn, we don't block. */
const negativeNote = (balanceAfter: number) => `Leaves a balance of ${balanceAfter.toFixed(1)} hrs`;

const HRTimeOff: React.FC<Props> = ({
  me, admin, employees, myRequests, pendingRequests, ptoTypes, busy, onSubmit, onRespond, onCancel,
}) => {
  const [typeId, setTypeId] = useState(ptoTypes[0] ? String(ptoTypes[0].id) : '');
  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');
  const [hours, setHours] = useState('8');
  const [comments, setComments] = useState('');

  // A type can be deleted in Settings while this form is open — fall back to the first one.
  const selectedType = ptoTypes.find(t => String(t.id) === typeId) ?? ptoTypes[0];
  const hoursValue = Number(hours) || 0;
  const datesInvalid = !!start && !!end && end < start;
  const balanceAfter = me.ptoBalanceHours - hoursValue;
  const canSubmit = !!selectedType && !!start && !!end && !datesInvalid && hoursValue > 0 && !busy;

  const submit = async () => {
    if (!canSubmit || !selectedType) return;
    const saved = await onSubmit({ ptoTypeId: selectedType.id, startDate: start, endDate: end, hours: hoursValue, comments });
    if (saved) {
      setStart(''); setEnd(''); setComments(''); setHours('8');
    }
  };

  return (
    <div className="hr-to">
      <div className="hr-to__cols">
        <section className="hr-to__form">
          <h3 className="hr-to__title">Request time off</h3>
          <div className={`hr-to__balance${me.ptoBalanceHours < 0 ? ' hr-to__balance--negative' : ''}`}>
            {me.ptoBalanceHours.toFixed(1)} hrs PTO balance
          </div>
          {ptoTypes.length === 0 ? (
            <p className="hr-dash__empty">No time off types are set up yet — ask your agency owner to add one in HR Settings.</p>
          ) : (
            <>
              <label className="hr-to__field">
                Type
                <select value={selectedType ? String(selectedType.id) : ''} onChange={e => setTypeId(e.target.value)}>
                  {ptoTypes.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                </select>
              </label>
              <div className="hr-to__field-row">
                <label className="hr-to__field">Starts<input type="date" value={start} onChange={e => setStart(e.target.value)} /></label>
                <label className="hr-to__field">Ends<input type="date" value={end} min={start || undefined} onChange={e => setEnd(e.target.value)} /></label>
              </div>
              {datesInvalid && <p className="hr-to__hint hr-to__hint--error">End date can't be before the start date.</p>}
              <label className="hr-to__field">
                Hours
                <input type="number" min="0.5" step="0.5" value={hours} onChange={e => setHours(e.target.value)} />
              </label>
              {selectedType && !selectedType.deductsBalance && (
                <p className="hr-to__hint">{selectedType.name} doesn't use your PTO balance.</p>
              )}
              {selectedType?.deductsBalance && hoursValue > 0 && balanceAfter < 0 && (
                <p className="hr-to__hint hr-to__hint--warn">If approved: {negativeNote(balanceAfter).toLowerCase()}.</p>
              )}
              <label className="hr-to__field">
                Comments
                <textarea value={comments} onChange={e => setComments(e.target.value)} rows={2} maxLength={1000} placeholder="comments" />
              </label>
              <button type="button" className="hr-to__submit" disabled={!canSubmit} onClick={submit}>Submit</button>
            </>
          )}
        </section>

        <section className="hr-to__history">
          <h3 className="hr-to__title">Your history</h3>
          {myRequests.length === 0 ? (
            <p className="hr-dash__empty">No requests yet.</p>
          ) : (
            <ul className="hr-to__list">
              {myRequests.map(r => (
                <li key={r.id} className={`hr-to__row hr-to__row--${r.status}`}>
                  <span className="hr-to__row-main">
                    <span><strong>{r.hours}h</strong> {r.typeName} · {dateRange(r)}</span>
                    {r.comments && <span className="hr-to__row-comment">{r.comments}</span>}
                    {r.decidedByName && (r.status === 'approved' || r.status === 'denied') && (
                      <span className="hr-to__row-comment">{r.status === 'approved' ? 'Approved' : 'Denied'} by {r.decidedByName}</span>
                    )}
                  </span>
                  <span className="hr-to__actions">
                    {r.status === 'pending' && (
                      <button type="button" className="hr-to__cancel" disabled={busy} onClick={() => onCancel(r.id)}>Cancel</button>
                    )}
                    <span className={`hr-to__status hr-to__status--${r.status}`}>{r.status}</span>
                  </span>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>

      {admin && (
        <section className="hr-to__inbox">
          <h3 className="hr-to__title">Requests {pendingRequests.length > 0 && <span className="hr__tab-badge">{pendingRequests.length}</span>}</h3>
          {pendingRequests.length === 0 ? (
            <p className="hr-dash__empty">Woohoo! No requests to worry about.</p>
          ) : (
            <ul className="hr-to__list">
              {pendingRequests.map(r => {
                const emp = employees.find(e => e.id === r.employeeId);
                const after = emp && r.deductsBalance ? emp.ptoBalanceHours - r.hours : null;
                return (
                  <li key={r.id} className="hr-to__row">
                    <span className="hr-to__row-main">
                      <span><strong>{r.employeeName}</strong> — {r.hours}h {r.typeName} · {dateRange(r)}</span>
                      {r.comments && <span className="hr-to__row-comment">{r.comments}</span>}
                      {after !== null && (
                        <span className={`hr-to__row-comment${after < 0 ? ' hr-to__row-comment--warn' : ''}`}>
                          {after < 0 ? negativeNote(after) : `Balance after approval: ${after.toFixed(1)} hrs`}
                        </span>
                      )}
                    </span>
                    <span className="hr-to__actions">
                      <button type="button" className="hr-to__approve" disabled={busy} onClick={() => onRespond(r.id, 'approved')}><FiCheck size={12} /> Approve</button>
                      <button type="button" className="hr-to__deny" disabled={busy} onClick={() => onRespond(r.id, 'denied')}><FiX size={12} /> Deny</button>
                    </span>
                  </li>
                );
              })}
            </ul>
          )}
        </section>
      )}
    </div>
  );
};

export default HRTimeOff;
